/**
 * AdminConfigPage — Consolidated System Settings
 *
 * Tab 0: System Configuration  — runtime, logging, storage settings
 * Tab 1: Prompt Management     — CPT templates, global prompts, merged preview
 * Tab 2: AI & Speech Services  — STT, LLM providers, model routing, AI Copilot (sub-tabs)
 * Tab 3: CPT Classification    — modality-first pipeline, A/B comparison, traffic split
 * Tab 4: Security & Compliance — auth, encryption, PHI, retention, KG
 *
 * Sidebar navigation passes ?tab=N to deep-link into sections.
 * Each tab loads lazily and preserves unsaved changes independently.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Computer as SystemIcon,
  Description as TemplateIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  RestartAlt as ResetIcon,
  Psychology as PsychologyIcon,
  Shield as SecurityIcon,
  Mic as MicIcon,
  AltRoute as RoutingIcon,
  SmartToy as CopilotIcon,
  Tune as TuneIcon,
  Assessment as AssessmentIcon,
  MedicalServices as ClinicalIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import PageHeader from '../components/PageHeader';
import systemConfigService, {
  ConfigItem,
  ConfigGroup,
} from '../services/systemConfigService';
import { fetchAIConfig } from '../store/aiServicesSlice';
import STTSettingsPanel from './admin/ai-services/STTSettingsPanel';
import LLMProviderSettingsPanel from './admin/ai-services/LLMProviderSettingsPanel';
import ModelRoutingPanel from './admin/ai-services/ModelRoutingPanel';
import PromptManagementPage from './admin/PromptManagementPage';
import AICopilotSettingsPanel from './admin/ai-services/AICopilotSettingsPanel';
import { typography } from '~/theme';

// ── Shared TabPanel ──────────────────────────────────────────────────

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  if (value !== index) return null; // lazy — only mount active tab
  return (
    <Box role="tabpanel" sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      {children}
    </Box>
  );
}

// ── Snackbar state helper ────────────────────────────────────────────

type SnackState = { open: boolean; message: string; severity: 'success' | 'error' | 'info' };
const SNACK_INIT: SnackState = { open: false, message: '', severity: 'info' };

// =====================================================================
// Component
// =====================================================================

// ── Tab descriptions for UX clarity ───────────────────────────────
const TAB_DESCRIPTIONS: Record<number, string> = {
  0: 'Configure runtime environment, logging, and storage settings.',
  1: 'Manage CPT templates, global prompts, and preview merged prompts.',
  2: 'Configure speech-to-text, LLM providers, model routing, and AI Copilot.',
  3: 'Manage CPT classification pipeline: enable modality-first classifier, A/B comparison, and traffic split.',
  4: 'Manage authentication, encryption, PHI access logging, data retention, and knowledge graph settings.',
  5: 'Configure clinical reporting settings: grading systems, enforcement mode, and confidence thresholds.',
};

const AdminConfigPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const canGoBackInApp = Boolean(location.key && location.key !== 'default');
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const isAdmin = user?.role === 'admin';

  // ── Top-level tab (6 tabs: System Config, Templates, AI & Speech, Classification, Security, Clinical) ──
  const initialTab = Math.min(parseInt(searchParams.get('tab') || '0', 10) || 0, 5);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [aiSubTab, setAiSubTab] = useState(0); // sub-tab within AI & Speech
  const [snackbar, setSnackbar] = useState<SnackState>(SNACK_INIT);
  const showSnack = (message: string, severity: SnackState['severity']) =>
    setSnackbar({ open: true, message, severity });

  // Sync tab with URL ?tab= param when navigating via sidebar
  useEffect(() => {
    const urlTab = Math.min(parseInt(searchParams.get('tab') || '0', 10) || 0, 5);
    if (urlTab !== activeTab) {
      setActiveTab(urlTab);
      setVisited((prev) => ({ ...prev, [urlTab]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Track which tabs have been visited (for lazy loading)
  const [visited, setVisited] = useState<Record<number, boolean>>({ [initialTab]: true });
  const handleTabChange = (_: unknown, v: number) => {
    setActiveTab(v);
    setVisited((prev) => ({ ...prev, [v]: true }));
  };

  // ── Admin guard ────────────────────────────────────────────────────
  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/');
  }, [user, navigate]);

  // ==================================================================
  // TAB 0 — System Settings
  // ==================================================================
  const [sysConfigs, setSysConfigs] = useState<ConfigItem[]>([]);
  const [sysGroups, setSysGroups] = useState<ConfigGroup[]>([]);
  const [sysChanges, setSysChanges] = useState<Record<string, string>>({});
  const [showSensitive, setShowSensitive] = useState<Record<string, boolean>>({});
  const [sysLoading, setSysLoading] = useState(false);


  const sysHasChanges = Object.keys(sysChanges).length > 0;

  const loadSysConfigs = useCallback(async () => {
    try {
      setSysLoading(true);
      const [configs, groups] = await Promise.all([
        systemConfigService.getAllConfig(),
        systemConfigService.getMetadata(),
      ]);
      setSysConfigs(configs);
      setSysGroups(groups);
    } catch {
      showSnack('Failed to load system configurations', 'error');
    } finally {
      setSysLoading(false);
    }
  }, []);

  // Load sys configs for Tab 0 (System Config), Tab 2 (AI & Speech Advanced), Tab 3 (Classification), Tab 4 (Security & Compliance), and Tab 5 (Clinical Reporting)
  useEffect(() => {
    if (visited[0] || visited[2] || visited[3] || visited[4] || visited[5]) loadSysConfigs();
  }, [visited, loadSysConfigs]);

  const handleSysConfigChange = (key: string, value: string) => {
    setSysChanges((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetToDefault = async (key: string) => {
    try {
      setSysLoading(true);
      await systemConfigService.resetConfig(key);
      showSnack(`${key} reset to default`, 'success');
      // Remove from pending changes if present
      setSysChanges((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      await loadSysConfigs();
    } catch (err) {
      const axErr = err as { response?: { data?: { detail?: string } } };
      showSnack(axErr.response?.data?.detail || 'Failed to reset', 'error');
    } finally {
      setSysLoading(false);
    }
  };

  const saveSysConfigs = async () => {
    try {
      setSysLoading(true);
      
      const configCount = Object.keys(sysChanges).length;
      const total = configCount;
      
      // Validation: Check if there are any changes to save
      if (total === 0) {
        console.warn('[AdminConfigPage] Save called with no changes');
        showSnack('No changes to save', 'info');
        return;
      }
      
      console.log(`[AdminConfigPage] Saving ${configCount} config(s)`);
      
      for (const [key, value] of Object.entries(sysChanges)) {
        console.log(`[AdminConfigPage] Updating config ${key} = ${value}`);
        await systemConfigService.updateConfig(key, value);
      }
      
      console.log('[AdminConfigPage] Save successful');
      showSnack(`Saved ${total} change(s)`, 'success');
      setSysChanges({});
      await loadSysConfigs();
    } catch (err) {
      // Comprehensive error logging
      console.error('[AdminConfigPage] Save failed:', err);
      
      const axErr = err as { response?: { status?: number; data?: { detail?: string } }; message?: string };
      
      let errorMessage = 'Failed to save';
      if (axErr.response) {
        const status = axErr.response.status;
        const detail = axErr.response.data?.detail;
        
        console.error(`[AdminConfigPage] HTTP ${status} error:`, detail);
        
        if (status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (status === 403) {
          errorMessage = 'Permission denied. Admin access required.';
        } else if (status === 422) {
          errorMessage = `Validation error: ${detail || 'Invalid value'}`;
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again or contact support.';
        } else if (detail) {
          errorMessage = detail;
        }
      } else if (axErr.message) {
        console.error('[AdminConfigPage] Network or client error:', axErr.message);
        errorMessage = `Network error: ${axErr.message}`;
      }
      
      showSnack(errorMessage, 'error');
    } finally {
      setSysLoading(false);
    }
  };

  const reloadSysConfig = async () => {
    try {
      setSysLoading(true);
      console.log('[AdminConfigPage] Reloading configuration');
      const result = await systemConfigService.reloadConfig();
      console.log('[AdminConfigPage] Reload successful:', result);
      showSnack(result.detail, 'success');
      await loadSysConfigs();
    } catch (error) {
      console.error('[AdminConfigPage] Reload failed:', error);
      const err = error as { response?: { data?: { detail?: string } }; message?: string };
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to reload configuration';
      showSnack(errorMessage, 'error');
    } finally {
      setSysLoading(false);
    }
  };

  const getSysConfigValue = (config: ConfigItem) => sysChanges[config.key] ?? config.value;

  // Build a lookup from config key → ConfigItem for the grouped view
  const configByKey = (Array.isArray(sysConfigs) ? sysConfigs : []).reduce((acc, c) => {
    acc[c.key] = c;
    return acc;
  }, {} as Record<string, ConfigItem>);

  // DEPRECATED keys — hidden from UI because they have no operational effect
  // when ENABLE_DEFERRED_CORRECTION is enabled (the default). Retained in
  // backend config for legacy testing only.
  const HIDDEN_DEPRECATED_KEYS = new Set([
    'ENABLE_CORRECTION_PATCH',
    'REFINEMENT_CHARACTER_COUNT_ENABLED',
    'REFINEMENT_CHUNK_SIZE_CHARS',
  ]);

  // Render all sub-groups for a given config group as accordions
  const renderConfigGroup = (groupId: string) => {
    const groups = Array.isArray(sysGroups) ? sysGroups : [];
    const group = groups.find((g) => g.id === groupId);
    if (sysLoading && (!Array.isArray(sysConfigs) || sysConfigs.length === 0)) {
      return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
    }
    if (!group) {
      return <Typography color="text.secondary" sx={{ p: 2 }}>No configuration data available.</Typography>;
    }
    return (
      <Box sx={{ p: 2, overflow: 'auto', height: '100%' }}>
        <Stack spacing={1.5}>
          {group.sub_groups.map((sg) => (
            <Accordion key={sg.id} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={typography.label.fontWeight}>{sg.label}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {sg.keys.map((keyMeta) => {
                    if (HIDDEN_DEPRECATED_KEYS.has(keyMeta.key)) return null;
                    const config = configByKey[keyMeta.key];
                    if (!config) return null;
                    const currentVal = getSysConfigValue(config);
                    const isModified = config.default != null && currentVal !== config.default;
                    const hasChoices = config.choices && config.choices.length > 0;
                    return (
                      <Grid item xs={12} md={6} key={config.key}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="subtitle2" color="text.secondary">{config.key}</Typography>
                                {isModified && (
                                  <Chip label="modified" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: typography.caption.fontSize }} />
                                )}
                                {keyMeta.requires_restart && (
                                  <Tooltip title="Requires application restart to take effect">
                                    <Chip label="restart" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: typography.caption.fontSize }} />
                                  </Tooltip>
                                )}
                              </Stack>
                              <Stack direction="row" spacing={0}>
                                {isModified && (
                                  <Tooltip title="Reset to default">
                                    <IconButton size="small" onClick={() => handleResetToDefault(config.key)} color="warning">
                                      <ResetIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {config.sensitive && (
                                  <IconButton size="small" onClick={() => setShowSensitive((prev) => ({ ...prev, [config.key]: !prev[config.key] }))}>
                                    {showSensitive[config.key] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                  </IconButton>
                                )}
                              </Stack>
                            </Box>
                            {hasChoices ? (
                              <>
                                <FormControl fullWidth size="small">
                                  <InputLabel id={`config-label-${config.key}`}>Value</InputLabel>
                                  <Select
                                    native
                                    labelId={`config-label-${config.key}`}
                                    value={currentVal}
                                    label="Value"
                                    onChange={(e) => handleSysConfigChange(config.key, e.target.value)}
                                  >
                                    {(config.choices ?? []).map((choice) => (
                                      <option key={choice} value={choice}>{choice}</option>
                                    ))}
                                  </Select>
                                </FormControl>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                  {config.description}
                                </Typography>
                              </>
                            ) : (
                              <TextField
                                fullWidth
                                size="small"
                                type={config.sensitive && !showSensitive[config.key] ? 'password' : (config.value_type === 'int' || config.value_type === 'float') ? 'number' : 'text'}
                                value={currentVal}
                                onChange={(e) => handleSysConfigChange(config.key, e.target.value)}
                                helperText={config.description}
                                inputProps={{
                                  min: config.min_value ?? undefined,
                                  max: config.max_value ?? undefined,
                                }}
                              />
                            )}
                            {config.default && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Default: {config.default}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Box>
    );
  };

  // Load AI config when AI & Speech tab (tab 2) is first visited
  const [aiConfigLoaded, setAiConfigLoaded] = useState(false);
  useEffect(() => {
    if (!aiConfigLoaded && visited[2]) {
      dispatch(fetchAIConfig());
      setAiConfigLoaded(true);
    }
  }, [visited, dispatch, aiConfigLoaded]);

  // ── Aggregate unsaved indicator ────────────────────────────────────
  const anyUnsaved = sysHasChanges;

  // ── Access guard ───────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Access denied. Only administrators can access this page.</Alert>
      </Box>
    );
  }

  // ==================================================================
  // RENDER
  // ==================================================================
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PageHeader
        title="System Settings"
        subtitle="Configure all system, AI, template, and security settings"
        onBack={() => canGoBackInApp ? navigate(-1) : navigate('/')}
        backLabel="Back"
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            {anyUnsaved && <Chip label="Unsaved Changes" color="warning" size="small" />}
            {(activeTab === 0 || activeTab === 2 || activeTab === 3 || activeTab === 4 || activeTab === 5) && (
              <>
                <Tooltip title="Reload config from database">
                  <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={reloadSysConfig} disabled={sysLoading}>
                    Reload
                  </Button>
                </Tooltip>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={sysLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={saveSysConfigs}
                  disabled={sysLoading || !sysHasChanges}
                >
                  Save
                </Button>
              </>
            )}
          </Stack>
        }
      />

      {/* ── Tab bar (4 logical sections) ─────────────────────────────── */}
      <Paper sx={{ flexShrink: 0 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab
            icon={<SystemIcon />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                System Configuration
                {sysHasChanges && <Chip label="modified" size="small" color="warning" sx={{ height: 18, fontSize: typography.caption.fontSize }} />}
              </Box>
            }
          />
          <Tab icon={<TemplateIcon />} iconPosition="start" label="Prompt Management" />
          <Tab icon={<PsychologyIcon />} iconPosition="start" label="AI & Speech Services" />
          <Tab icon={<AssessmentIcon />} iconPosition="start" label="CPT Classification" />
          <Tab icon={<SecurityIcon />} iconPosition="start" label="Security & Compliance" />
          <Tab icon={<ClinicalIcon />} iconPosition="start" label="Clinical Reporting" />
        </Tabs>
      </Paper>
      {/* Tab description for UX clarity */}
      <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 0.75, flexShrink: 0 }}>
        {TAB_DESCRIPTIONS[activeTab]}
      </Typography>

      {/* ============================================================ */}
      {/* TAB 0 — System Configuration                                  */}
      {/* ============================================================ */}
      <TabPanel value={activeTab} index={0}>
        {renderConfigGroup('system')}
      </TabPanel>

      {/* ============================================================ */}
      {/* TAB 1 — Prompt Management                                      */}
      {/* ============================================================ */}
      <TabPanel value={activeTab} index={1}>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <PromptManagementPage />
        </Box>
      </TabPanel>

      {/* ============================================================ */}
      {/* TAB 2 — AI & Speech Services (sub-tabs)                      */}
      {/* ============================================================ */}
      <TabPanel value={activeTab} index={2}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Paper variant="outlined" sx={{ flexShrink: 0, mb: 1 }}>
            <Tabs
              value={aiSubTab}
              onChange={(_, v) => setAiSubTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: typography.caption.fontSize } }}
            >
              <Tab icon={<MicIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Speech-to-Text" />
              <Tab icon={<PsychologyIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="LLM Providers" />
              <Tab icon={<RoutingIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Model Routing" />
              <Tab icon={<CopilotIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="AI Copilot" />
            </Tabs>
          </Paper>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {aiSubTab === 0 && <STTSettingsPanel onSnack={showSnack} />}
            {aiSubTab === 1 && <LLMProviderSettingsPanel onSnack={showSnack} />}
            {aiSubTab === 2 && <ModelRoutingPanel onSnack={showSnack} />}
            {aiSubTab === 3 && <AICopilotSettingsPanel onSnack={showSnack} />}

            {/* Advanced Configuration — all ai_speech config keys */}
            <Divider sx={{ my: 2 }} />
            <Accordion defaultExpanded={false} sx={{ mx: 2, mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <TuneIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="subtitle1" fontWeight={typography.label.fontWeight}>Advanced Configuration</Typography>
                  <Chip label="All AI & Speech keys" size="small" variant="outlined" sx={{ height: 20, fontSize: typography.caption.fontSize }} />
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {renderConfigGroup('ai_speech')}
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </TabPanel>

      {/* ============================================================ */}
      {/* TAB 3 — CPT Classification                                   */}
      {/* ============================================================ */}
      <TabPanel value={activeTab} index={3}>
        {renderConfigGroup('classification')}
      </TabPanel>

      {/* ============================================================ */}
      {/* TAB 4 — Security & Compliance                                */}
      {/* ============================================================ */}
      <TabPanel value={activeTab} index={4}>
        {renderConfigGroup('security_compliance')}
      </TabPanel>

      {/* ============================================================ */}
      {/* TAB 5 — Clinical Reporting                                   */}
      {/* ============================================================ */}
      <TabPanel value={activeTab} index={5}>
        {renderConfigGroup('clinical')}
      </TabPanel>

      {/* ── Snackbar ────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(SNACK_INIT)}
      >
        <Alert onClose={() => setSnackbar(SNACK_INIT)} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminConfigPage;
